// Full pre-flight edit dialog for the Vermittlungsauftrag.
//
// Batch AJ-6 — replaces the tiny Laufzeit + Provision dialog with a
// form covering every user-mutable field in `VermittlungsauftragData`.
// The form is grouped into accordion sections that mirror the printed
// document's numbered headings, so the operator can fill in only the
// sections relevant to the current deal.
//
// All values are returned to the caller as a single `VermittlungsauftragData`-
// shaped payload. The caller is responsible for (a) passing it into
// the PDF renderer and (b) persisting it to `documents.metadata` so a
// re-render after signing reproduces the original content byte-identical
// to what the seller saw on the magic-link page.
//
// Pure presentation — no API calls. The form is initialised from a
// `defaults` prop that the caller derives from the linked client +
// property rows; the user-edited values overlay those defaults.
//
// Sections are intentionally collapsed by default except the two that
// are commonly required (6 Provisionsvereinbarung + 10 Bestätigungen),
// so the form opens compact and the operator only expands what they
// need to touch.

import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ChevronDown, FileText } from 'lucide-react';
import { formatDeDecimal, parseDeNumber } from '@/lib/parseDeNumber';

// ─── Public types ─────────────────────────────────────────────────────

/** Values the dialog manages. Mirrors a subset of
 *  `VermittlungsauftragData` (the user-editable parts). The caller
 *  spreads this onto the renderer payload + persists as-is into
 *  `documents.metadata`. */
export interface VermittlungsauftragFormValues {
  // Top level
  /** Migration 0058 — which contract to produce. 'allein' keeps the
   *  historical behaviour (exclusive, fixed term); 'einfach' produces the
   *  ordinary non-exclusive order. Persisted BOTH on documents.contract_variant
   *  (what the daily report counts) and inside documents.metadata (what a
   *  re-render reads), so the two can never disagree about the same document. */
  contractVariant: 'allein' | 'einfach';
  provisionNetPercent: number | null;
  /** Only printed for 'allein' — an ordinary order is open-ended. */
  exclusivityMonths: number;
  gerichtsstand: string | null;
  // Section 1 — Auftraggeber (the rest comes auto-filled from client)
  seller: {
    plz: string | null;
    konsument: boolean | null;
    eigentumsstatus: 'eigentuemer' | 'verfuegung' | 'vollmacht' | null;
  };
  // Sections 3, 4, 7 — Object + Kaufobjekt + Energieausweis fields
  property: {
    baujahr: number | null;
    beziehbar_ab: string | null;
    verbrauchsabhaengige_kosten: number | null;
    verbrauchs_strom: boolean;
    verbrauchs_heizung: boolean;
    verbrauchs_warmwasser: boolean;
    sonstiges_objekt: string | null;
    eigentumsform: 'allein' | 'wohnung' | 'anteil' | 'sonstiges' | null;
    betriebskosten_monatlich: number | null;
    reparaturfonds: number | null;
    hausverwaltung: string | null;
    im_kaufpreis_enthalten: string | null;
    lasten_bekannt: 'keine' | 'folgende' | null;
    lasten_details: string | null;
    maengel_bekannt: 'keine' | 'folgende' | null;
    maengel_details: string | null;
    hwb_kennwert: number | null;
    fgee_kennwert: number | null;
    energieausweis_status:
      | 'informiert_keine_pflicht'
      | 'vorhanden'
      | 'wird_vorgelegt'
      | 'nicht_vorgelegt'
      | null;
  };
  // Section 10 — confirmations (6 booleans, true=ja / false=nein / null=blank)
  bestaetigung: {
    geschaeftsraeume: boolean | null;
    ueber_ruecktritt: boolean | null;
    muster_formular: boolean | null;
    fagg_verpflichtung: boolean | null;
    datentraeger: boolean | null;
    kopie: boolean | null;
  };
  // Section 11 — FAGG vorzeitiges Tätigwerden
  faggVorzeitig: boolean | null;
}

export const DEFAULT_VERMITTLUNGSAUFTRAG_FORM: VermittlungsauftragFormValues = {
  contractVariant: 'allein',
  provisionNetPercent: null,
  exclusivityMonths: 6,
  gerichtsstand: null,
  seller: { plz: null, konsument: null, eigentumsstatus: null },
  property: {
    baujahr: null,
    beziehbar_ab: null,
    verbrauchsabhaengige_kosten: null,
    verbrauchs_strom: false,
    verbrauchs_heizung: false,
    verbrauchs_warmwasser: false,
    sonstiges_objekt: null,
    eigentumsform: null,
    betriebskosten_monatlich: null,
    reparaturfonds: null,
    hausverwaltung: null,
    im_kaufpreis_enthalten: null,
    lasten_bekannt: null,
    lasten_details: null,
    maengel_bekannt: null,
    maengel_details: null,
    hwb_kennwert: null,
    fgee_kennwert: null,
    energieausweis_status: null,
  },
  bestaetigung: {
    geschaeftsraeume: null,
    ueber_ruecktritt: null,
    muster_formular: null,
    fagg_verpflichtung: null,
    datentraeger: null,
    kopie: null,
  },
  faggVorzeitig: null,
};

interface Props {
  open: boolean;
  defaults: VermittlungsauftragFormValues;
  onCancel: () => void;
  onGenerate: (values: VermittlungsauftragFormValues) => void;
}

// ─── Small form primitives (local to keep the file self-contained) ───

function TextField({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  type?: 'text' | 'email';
}) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-ink-2">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="h-9 rounded-md border border-line bg-card px-2.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brand-gold))]/40"
      />
    </label>
  );
}

function NumberField({
  label,
  value,
  onChange,
  placeholder,
  suffix,
}: {
  label: string;
  value: number | null;
  onChange: (v: number | null) => void;
  placeholder?: string;
  suffix?: string;
}) {
  // The typed text is the source of truth while the user types. The input used
  // to redisplay the parsed number on every keystroke, and "2," parses to 2, so
  // the comma vanished the moment it was typed: "2,5" became 25 % Provision.
  const [text, setText] = useState(() => formatDeDecimal(value));

  // Re-seed only when the value changed from outside (the dialog re-opened with
  // new defaults), never when it is the echo of what was just typed.
  useEffect(() => {
    if (parseDeNumber(text) !== value) setText(formatDeDecimal(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-ink-2">
        {label}
        {suffix && <span className="ms-1 text-ink-3">({suffix})</span>}
      </span>
      <input
        type="text"
        inputMode="decimal"
        value={text}
        onChange={(e) => {
          setText(e.target.value);
          onChange(parseDeNumber(e.target.value));
        }}
        // On leaving the field, show the number as it was read, so "2.5" turns
        // into "2,5" and text that is not a number clears instead of lingering.
        onBlur={() => setText(formatDeDecimal(value))}
        placeholder={placeholder}
        className="h-9 rounded-md border border-line bg-card px-2.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brand-gold))]/40"
      />
    </label>
  );
}

function SelectField<T extends string>({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: T | null;
  onChange: (v: T | null) => void;
  options: Array<{ value: T; label: string }>;
}) {
  return (
    <label className="flex flex-col gap-1 text-xs">
      <span className="text-ink-2">{label}</span>
      <select
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value ? (e.target.value as T) : null)}
        className="h-9 rounded-md border border-line bg-card px-2.5 text-sm text-ink focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brand-gold))]/40"
      >
        <option value="">—</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function BoolField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean | null;
  onChange: (v: boolean | null) => void;
}) {
  return (
    <div className="flex flex-col gap-1 text-xs">
      <span className="text-ink-2">{label}</span>
      <div className="flex gap-1">
        {(
          [
            { v: true, l: 'Ja' },
            { v: false, l: 'Nein' },
            { v: null, l: '—' },
          ] as const
        ).map((o) => (
          <button
            key={String(o.v)}
            type="button"
            onClick={() => onChange(o.v)}
            className={
              value === o.v
                ? 'flex-1 rounded-md border border-[hsl(var(--brand-gold-deep))] bg-[hsl(var(--brand-gold))]/15 px-2 py-1 text-xs font-semibold text-ink'
                : 'flex-1 rounded-md border border-line bg-card px-2 py-1 text-xs text-ink-2 hover:bg-paper-2'
            }
          >
            {o.l}
          </button>
        ))}
      </div>
    </div>
  );
}

function Checkbox({
  label,
  value,
  onChange,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 text-xs">
      <input
        type="checkbox"
        checked={value}
        onChange={(e) => onChange(e.target.checked)}
        className="h-4 w-4 rounded border-line text-[hsl(var(--brand-gold-deep))] focus:ring-[hsl(var(--brand-gold))]/40"
      />
      <span className="text-ink-2">{label}</span>
    </label>
  );
}

function Section({
  title,
  children,
  defaultOpen = false,
}: {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  return (
    <details
      open={defaultOpen}
      className="group rounded-lg border border-line bg-paper-2/50"
    >
      <summary className="flex cursor-pointer items-center justify-between gap-2 px-3 py-2 text-sm font-semibold text-ink">
        <span>{title}</span>
        <ChevronDown className="h-4 w-4 transition-transform group-open:rotate-180" />
      </summary>
      <div className="border-t border-line px-3 py-3">{children}</div>
    </details>
  );
}

// ─── Main dialog ──────────────────────────────────────────────────────

export function VermittlungsauftragEditDialog({
  open,
  defaults,
  onCancel,
  onGenerate,
}: Props) {
  const { t } = useTranslation();
  const [form, setForm] = useState<VermittlungsauftragFormValues>(defaults);

  // Refresh local state whenever the dialog re-opens with new defaults
  // (e.g. user closes & re-opens after editing the underlying client).
  useEffect(() => {
    if (open) setForm(defaults);
  }, [open, defaults]);

  if (!open) return null;

  // Top-level helpers so the deeply-nested setForm calls stay readable.
  const set = <K extends keyof VermittlungsauftragFormValues>(
    key: K,
    value: VermittlungsauftragFormValues[K],
  ) => setForm((f) => ({ ...f, [key]: value }));
  const setSeller = (patch: Partial<VermittlungsauftragFormValues['seller']>) =>
    setForm((f) => ({ ...f, seller: { ...f.seller, ...patch } }));
  const setProperty = (patch: Partial<VermittlungsauftragFormValues['property']>) =>
    setForm((f) => ({ ...f, property: { ...f.property, ...patch } }));
  const setBestaetigung = (
    patch: Partial<VermittlungsauftragFormValues['bestaetigung']>,
  ) => setForm((f) => ({ ...f, bestaetigung: { ...f.bestaetigung, ...patch } }));

  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 p-4">
      <div className="flex max-h-[calc(100vh-2rem)] w-full max-w-3xl flex-col rounded-xl border border-line bg-paper shadow-soft">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-line px-5 py-4">
          <div>
            <h3 className="font-display text-lg text-ink">
              {t('docs.vermittlung.full_options_title', 'Vermittlungsauftrag — Felder bearbeiten')}
            </h3>
            <p className="mt-1 text-xs text-ink-3">
              {/* The ordinary order has no Laufzeit field at all, so the
                  original subtitle would name a required field the operator
                  cannot see. */}
              {form.contractVariant === 'einfach'
                ? t(
                    'docs.vermittlung.full_options_subtitle_einfach',
                    'Felder, die hier leer bleiben, werden im PDF als ausfüllbare Linie gedruckt. Beim einfachen Auftrag wird keine Laufzeit gedruckt.',
                  )
                : t(
                    'docs.vermittlung.full_options_subtitle',
                    'Felder, die hier leer bleiben, werden im PDF als ausfüllbare Linie gedruckt. Provision und Laufzeit sind erforderlich.',
                  )}
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md p-1 text-ink-3 transition hover:bg-paper-2 hover:text-ink"
          >
            <span className="text-lg leading-none">×</span>
          </button>
        </div>

        {/* Body — scrollable */}
        <div className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
          {/* ─── Always-visible essentials ─── */}
          {/* The contract variant sits first because it changes what the rest
              of the form means: an ordinary order has no term, so the Laufzeit
              select disappears rather than collecting a number the PDF then
              silently drops. */}
          <div className="rounded-lg border border-[hsl(var(--brand-gold-deep))]/40 bg-[hsl(var(--brand-gold))]/8 p-3">
            <div className="mb-3">
              {/* A bare <label> cannot name a group of buttons, so the choice
                  is a real radiogroup: the label is its accessible name and
                  each option reports its own checked state. */}
              <span
                id="va-variant-label"
                className="mb-1.5 block text-xs font-medium text-ink-2"
              >
                {t('docs.vermittlung.variant_label', 'Vertragsart')}
              </span>
              <div
                role="radiogroup"
                aria-labelledby="va-variant-label"
                className="grid grid-cols-1 gap-2 sm:grid-cols-2"
              >
                {(
                  [
                    {
                      value: 'allein' as const,
                      label: t(
                        'docs.vermittlung.variant_allein',
                        'Alleinvermittlungsauftrag (exklusiv)',
                      ),
                      hint: t(
                        'docs.vermittlung.variant_allein_hint',
                        'Exklusiv für 4 M Immobilien, befristet auf die gewählte Laufzeit.',
                      ),
                    },
                    {
                      value: 'einfach' as const,
                      label: t(
                        'docs.vermittlung.variant_einfach',
                        'Vermittlungsauftrag (einfach)',
                      ),
                      hint: t(
                        'docs.vermittlung.variant_einfach_hint',
                        'Ohne Exklusivität und unbefristet: Der Auftraggeber darf selbst verkaufen oder weitere Makler beauftragen.',
                      ),
                    },
                  ]
                ).map((opt) => {
                  const active = form.contractVariant === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => set('contractVariant', opt.value)}
                      role="radio"
                      aria-checked={active}
                      className={
                        'rounded-lg border px-3 py-2 text-start transition ' +
                        (active
                          ? 'border-[hsl(var(--brand-gold-deep))] bg-card shadow-soft'
                          : 'border-line bg-card/60 hover:bg-card')
                      }
                    >
                      <span className="flex items-center gap-2">
                        <span
                          className={
                            'inline-block h-3 w-3 shrink-0 rounded-full border ' +
                            (active
                              ? 'border-[hsl(var(--brand-gold-deep))] bg-[hsl(var(--brand-gold))]'
                              : 'border-line')
                          }
                        />
                        <span className="text-sm font-medium text-ink">{opt.label}</span>
                      </span>
                      <span className="mt-1 block ps-5 text-[11px] leading-snug text-ink-3">
                        {opt.hint}
                      </span>
                    </button>
                  );
                })}
              </div>
              <p className="mt-1.5 text-[11px] text-ink-3">
                {t(
                  'docs.vermittlung.variant_locked',
                  'Die Vertragsart wird mit dem Dokument gespeichert und kann nachträglich nicht geändert werden.',
                )}
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {form.contractVariant === 'allein' ? (
                <SelectField
                  label={t(
                    'docs.vermittlung.opt_months',
                    'Vertragslaufzeit (max. 6 Monate)',
                  )}
                  value={String(form.exclusivityMonths) as '1' | '2' | '3' | '4' | '5' | '6'}
                  onChange={(v) =>
                    set('exclusivityMonths', v ? Number(v) : 6)
                  }
                  options={[1, 2, 3, 4, 5, 6].map((m) => ({
                    value: String(m) as '1' | '2' | '3' | '4' | '5' | '6',
                    label: `${m} ${m === 1 ? 'Monat' : 'Monate'}`,
                  }))}
                />
              ) : (
                <div className="flex items-end">
                  <p className="text-[11px] leading-snug text-ink-3">
                    {t(
                      'docs.vermittlung.no_term_hint',
                      'Einfacher Auftrag: unbefristet — es wird keine Laufzeit gedruckt.',
                    )}
                  </p>
                </div>
              )}
              <NumberField
                label={t('docs.vermittlung.opt_provision', 'Provision (netto, %)')}
                value={form.provisionNetPercent}
                onChange={(v) => set('provisionNetPercent', v)}
                placeholder="z. B. 0,5 · 1 · 2,5 · 3 — leer = individuell"
                suffix="leer = individuelle Vereinbarung"
              />
            </div>
          </div>

          {/* ─── Section 1 — Auftraggeber ─── */}
          <Section title="1. Auftraggeber (zusätzliche Angaben)">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <TextField
                label="PLZ"
                value={form.seller.plz ?? ''}
                onChange={(v) => setSeller({ plz: v || null })}
              />
              <BoolField
                label="Konsument/in (KSchG)"
                value={form.seller.konsument}
                onChange={(v) => setSeller({ konsument: v })}
              />
              <SelectField
                label="Eigentumsstatus"
                value={form.seller.eigentumsstatus}
                onChange={(v) => setSeller({ eigentumsstatus: v })}
                options={[
                  { value: 'eigentuemer', label: 'Objekteigentümer' },
                  { value: 'verfuegung', label: 'Verfügungsberechtigter' },
                  { value: 'vollmacht', label: 'Notariell beglaub. Vollmacht' },
                ]}
              />
            </div>
          </Section>

          {/* ─── Section 3 — Vermittlungsobjekt ─── */}
          <Section title="3. Vermittlungsobjekt">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <NumberField
                label="Baujahr"
                value={form.property.baujahr}
                onChange={(v) => setProperty({ baujahr: v })}
                placeholder="z. B. 1925"
              />
              <TextField
                label="Beziehbar ab"
                value={form.property.beziehbar_ab ?? ''}
                onChange={(v) => setProperty({ beziehbar_ab: v || null })}
                placeholder="z. B. sofort · 01.10.2026"
              />
              <NumberField
                label="Verbrauchsabhängige Kosten (mtl.)"
                value={form.property.verbrauchsabhaengige_kosten}
                onChange={(v) => setProperty({ verbrauchsabhaengige_kosten: v })}
                suffix="EUR"
              />
              <div className="flex flex-col gap-1.5 text-xs">
                <span className="text-ink-2">Enthalten in Kosten</span>
                <div className="flex flex-col gap-1.5 rounded-md border border-line bg-card px-2.5 py-2">
                  <Checkbox
                    label="Strom / Gas"
                    value={form.property.verbrauchs_strom}
                    onChange={(v) => setProperty({ verbrauchs_strom: v })}
                  />
                  <Checkbox
                    label="Heizung"
                    value={form.property.verbrauchs_heizung}
                    onChange={(v) => setProperty({ verbrauchs_heizung: v })}
                  />
                  <Checkbox
                    label="Warmwasser"
                    value={form.property.verbrauchs_warmwasser}
                    onChange={(v) => setProperty({ verbrauchs_warmwasser: v })}
                  />
                </div>
              </div>
            </div>
            <div className="mt-3">
              <TextField
                label="Sonstiges zum Objekt"
                value={form.property.sonstiges_objekt ?? ''}
                onChange={(v) => setProperty({ sonstiges_objekt: v || null })}
                placeholder="Freitext"
              />
            </div>
          </Section>

          {/* ─── Section 4 — Kaufobjekt ─── */}
          <Section title="4. Kaufobjekt">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <SelectField
                label="Eigentumsform"
                value={form.property.eigentumsform}
                onChange={(v) => setProperty({ eigentumsform: v })}
                options={[
                  { value: 'allein', label: 'Alleineigentum' },
                  { value: 'wohnung', label: 'Wohnungseigentum' },
                  { value: 'anteil', label: 'Anteilseigentum' },
                  { value: 'sonstiges', label: 'Sonstiges' },
                ]}
              />
              <NumberField
                label="Betriebskosten mtl. (inkl. MwSt)"
                value={form.property.betriebskosten_monatlich}
                onChange={(v) => setProperty({ betriebskosten_monatlich: v })}
                suffix="EUR"
              />
              <NumberField
                label="Reparaturfonds"
                value={form.property.reparaturfonds}
                onChange={(v) => setProperty({ reparaturfonds: v })}
                suffix="EUR"
              />
              <TextField
                label="Hausverwaltung"
                value={form.property.hausverwaltung ?? ''}
                onChange={(v) => setProperty({ hausverwaltung: v || null })}
                placeholder="Name + Adresse"
              />
              <div className="sm:col-span-2">
                <TextField
                  label="Im Kaufpreis enthalten"
                  value={form.property.im_kaufpreis_enthalten ?? ''}
                  onChange={(v) =>
                    setProperty({ im_kaufpreis_enthalten: v || null })
                  }
                  placeholder="z. B. Küche, Wohnbauförderung"
                />
              </div>
              <SelectField
                label="Lasten / Dienstbarkeiten bekannt"
                value={form.property.lasten_bekannt}
                onChange={(v) => setProperty({ lasten_bekannt: v })}
                options={[
                  { value: 'keine', label: 'Keine' },
                  { value: 'folgende', label: 'Folgende:' },
                ]}
              />
              <TextField
                label="Lasten — Details"
                value={form.property.lasten_details ?? ''}
                onChange={(v) => setProperty({ lasten_details: v || null })}
                placeholder="nur wenn „Folgende"
              />
              <SelectField
                label="Versteckte Mängel bekannt"
                value={form.property.maengel_bekannt}
                onChange={(v) => setProperty({ maengel_bekannt: v })}
                options={[
                  { value: 'keine', label: 'Keine' },
                  { value: 'folgende', label: 'Folgende:' },
                ]}
              />
              <TextField
                label="Mängel — Details"
                value={form.property.maengel_details ?? ''}
                onChange={(v) => setProperty({ maengel_details: v || null })}
                placeholder="nur wenn „Folgende"
              />
            </div>
          </Section>

          {/* ─── Section 7 — Energieausweis ─── */}
          <Section title="7. Energieausweis">
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <NumberField
                label="HWB-Kennwert"
                value={form.property.hwb_kennwert}
                onChange={(v) => setProperty({ hwb_kennwert: v })}
                suffix="kWh/m²a"
              />
              <NumberField
                label="fGEE-Kennwert"
                value={form.property.fgee_kennwert}
                onChange={(v) => setProperty({ fgee_kennwert: v })}
              />
              <SelectField
                label="Status"
                value={form.property.energieausweis_status}
                onChange={(v) => setProperty({ energieausweis_status: v })}
                options={[
                  {
                    value: 'informiert_keine_pflicht',
                    label: 'Informiert (keine Pflicht)',
                  },
                  { value: 'vorhanden', label: 'Vorhanden' },
                  { value: 'wird_vorgelegt', label: 'Wird vorgelegt' },
                  { value: 'nicht_vorgelegt', label: 'Nicht vorgelegt' },
                ]}
              />
            </div>
          </Section>

          {/* ─── Section 8 — Sonstige ─── */}
          <Section title="8. Sonstige Vereinbarungen">
            <TextField
              label="Gerichtsstand (8.6)"
              value={form.gerichtsstand ?? ''}
              onChange={(v) => set('gerichtsstand', v || null)}
              placeholder="z. B. Wien — Innere Stadt"
            />
          </Section>

          {/* ─── Section 10 — Bestätigungen ─── */}
          <Section title="10. Bestätigungen" defaultOpen>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              <BoolField
                label="10.1 In Geschäftsräumen abgeschlossen"
                value={form.bestaetigung.geschaeftsraeume}
                onChange={(v) => setBestaetigung({ geschaeftsraeume: v })}
              />
              <BoolField
                label="10.2 Über Rücktrittsrecht informiert"
                value={form.bestaetigung.ueber_ruecktritt}
                onChange={(v) => setBestaetigung({ ueber_ruecktritt: v })}
              />
              <BoolField
                label="10.3 Muster-Widerrufsformular ausgehändigt"
                value={form.bestaetigung.muster_formular}
                onChange={(v) => setBestaetigung({ muster_formular: v })}
              />
              <BoolField
                label="10.4 FAGG-Verpflichtung anerkannt"
                value={form.bestaetigung.fagg_verpflichtung}
                onChange={(v) => setBestaetigung({ fagg_verpflichtung: v })}
              />
              <BoolField
                label="10.5 Übermittlung auf Datenträger ok"
                value={form.bestaetigung.datentraeger}
                onChange={(v) => setBestaetigung({ datentraeger: v })}
              />
              <BoolField
                label="10.6 Kopie erhalten"
                value={form.bestaetigung.kopie}
                onChange={(v) => setBestaetigung({ kopie: v })}
              />
            </div>
          </Section>

          {/* ─── Section 11 — FAGG ─── */}
          <Section title="11. FAGG — vorzeitiges Tätigwerden">
            <BoolField
              label="Auftraggeber wünscht vorzeitiges Tätigwerden"
              value={form.faggVorzeitig}
              onChange={(v) => set('faggVorzeitig', v)}
            />
          </Section>
        </div>

        {/* Footer */}
        <div className="flex flex-col-reverse gap-2 border-t border-line px-5 py-3 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md border border-line bg-card px-3 py-1.5 text-sm font-medium hover:bg-paper-2"
          >
            {t('common.cancel', 'Abbrechen')}
          </button>
          <button
            type="button"
            onClick={() => onGenerate(form)}
            className="inline-flex items-center gap-1.5 rounded-md bg-ink px-4 py-1.5 text-sm font-semibold text-paper hover:bg-ink/90"
          >
            <FileText className="h-4 w-4" />
            {t('docs.vermittlung.opt_generate', 'Generieren')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
